import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

async function ExportToExcel({
    data,
    branches,
    branchId,
    filter,
    filterDateStart,
    filterDateEnd,
    t,
    profile
}: {
    data: any
    branches: any[]
    branchId: number
    filter: any
    filterDateStart: string
    filterDateEnd: string
    t: any
    profile?: any
}) {
    try {
        const workbook = new ExcelJS.Workbook()
        const sheet = workbook.addWorksheet(t('REPORT.REPORT_NAME.PATIENT_TREATMENT'))

        const titleRow = sheet.addRow([t('REPORT.REPORT_NAME.PATIENT_TREATMENT')])
        titleRow.font = { bold: true, size: 16 }
        sheet.mergeCells(titleRow.number, 1, titleRow.number, 20)
        titleRow.alignment = { horizontal: 'left' }

        // Filter info (horizontal)
        const filterLabels = [
            t('REPORT.FILTER.BRANCH'),
            t('USER_DOCTOR'),
            t('REPORT.FILTER.OPERATIVE_TYPE'),
            t('REPORT.TABLE.FOLLOW_UP.OPERATIVE'),
            t('REPORT.FILTER.DUPLICATE_PATIENT'),
            t('REPORT.FILTER.DATE_START'),
            t('REPORT.FILTER.DATE_END'),
        ]
        const filterValues = [
            branchId === 0 ? t('REPORT.FILTER.ALL') : _.get(_.find(branches, { branchId }), 'branchName', ''),
            filter.doctorNames || t('REPORT.FILTER.ALL'),
            filter.operativeTypeName || t('REPORT.FILTER.ALL'),
            filter.operativeName || t('REPORT.FILTER.ALL'),
            filter.duplicatePatient ? t('ไม่แสดงชื่อผู้ป่วยซ้ำ') : t('แสดงชื่อผู้ป่วยซ้ำ'),
            filterDateStart ? toBuddhistYear(moment(filterDateStart), 'DD/MM/YYYY') : '-',
            filterDateEnd ? toBuddhistYear(moment(filterDateEnd), 'DD/MM/YYYY') : '-',
        ]

        const filterRowData: any[] = []
        for (let i = 0; i < filterLabels.length; i++) {
            filterRowData.push(filterLabels[i] + ':', filterValues[i])
        }
        const filterRow = sheet.addRow(filterRowData)
        filterRow.font = { size: 11 }
        for (let col = 1; col <= filterRowData.length; col += 2) {
            filterRow.getCell(col).font = { bold: true }
            filterRow.getCell(col).alignment = { horizontal: 'left' }
            filterRow.getCell(col + 1).alignment = { horizontal: 'left' }
        }
        sheet.addRow([])

        const headCells = [
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.#NO'), width: 10 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.DATE'), width: 15 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.CN'), width: 15 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.PT'), width: 15 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.PATIENT_FULL_NAME'), width: 25 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.PHONE'), width: 25 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.DOCTOR_FULL_NAME'), width: 25 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.OPERATIVE_TYPE'), width: 20 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.OPERATIVE_CODE'), width: 15 },
            { label: t('REPORT.TABLE.PATIENT_TREATMENT.OPERATIVE'), width: 25 },
        ]
        if (profile?.hasShowPhoneNumber === "UNPERMISS") {
            headCells.splice(5, 1)
        }

        sheet.columns = headCells.map(c => ({ width: c.width }))

        const headerRow = sheet.addRow(headCells.map(c => c.label))
        headerRow.font = { bold: true, size: 12 }
        headerRow.alignment = { horizontal: 'left', vertical: 'middle' }

        // Group data by dateOrder
        const groupedData = _.groupBy(data.rows, 'dateOrder')

        if (!Array.isArray(data.rows) || data.rows.length === 0) {

            const noDataRow = sheet.addRow([t('REPORT.TITLE.NO_DATA')])
            sheet.mergeCells(noDataRow.number, 1, noDataRow.number, headCells.length)
            noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
            noDataRow.alignment = { horizontal: 'center' }
                ;
        } else {

            for (const [_dateOrder, rows] of Object.entries(groupedData)) {
                // Add data rows for this date
                for (const row of rows) {
                    const rowData = [
                        data.rows.indexOf(row) + 1,
                        row.date || '-',
                        row.cn || '-',
                        row.pt || '-',
                        row.patientFullName || '-',
                        row.phone || '-',
                        row.doctorFullName || '-',
                        row.operativeTypeName || '-',
                        row.operativeCode || '-',
                        row.operativeName || '-',
                    ]
                    if (profile?.hasShowPhoneNumber === "UNPERMISS") {
                        rowData.splice(5, 1)
                    }
                    sheet.addRow(rowData)
                }

                // Add empty row between date groups

                sheet.addRow([])
            }

        }
        // Summary table
        if (data.summary && Array.isArray(data.summary)) {
            sheet.addRow([])
            sheet.addRow([])

            const summaryTitleRow = sheet.addRow([t('ประเภทและรายการหัตถการ')])
            summaryTitleRow.font = { bold: true, size: 14 }
            sheet.mergeCells(summaryTitleRow.number, 1, summaryTitleRow.number, 20)
            summaryTitleRow.alignment = { horizontal: 'center' }

            const summaryHeaderRow = sheet.addRow([
                t('ประเภทและรายการหัตถการ'),
                t('จำนวน (ครั้ง)')
            ])
            summaryHeaderRow.font = { bold: true, size: 12 }
            summaryHeaderRow.alignment = { horizontal: 'center' }

            if (!Array.isArray(data.summary) || data.summary.length === 0) {
                const noDataRow = sheet.addRow([t('REPORT.TITLE.NO_DATA')])
                sheet.mergeCells(noDataRow.number, 1, noDataRow.number, 2)
                noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
                noDataRow.alignment = { horizontal: 'center' }
            } else {
                for (const summaryItem of data.summary) {
                    // Add operative type row
                    const operativeTypeRow = sheet.addRow([
                        summaryItem.operativeTypeName || '-',
                        summaryItem.countAll || 0
                    ])
                    operativeTypeRow.font = { bold: true }

                    // Add operative items
                    if (summaryItem.operatives && Array.isArray(summaryItem.operatives)) {
                        for (const operative of summaryItem.operatives) {
                            const operativeRow = sheet.addRow([
                                `  ${operative.operativeName || '-'}`,
                                operative.countOperative || 0
                            ])
                        }
                    }
                }
            }
        }

        forceTextOnDateCells(workbook)
        const buffer = await workbook.xlsx.writeBuffer()
        const timestamp = moment().format('YYYYMMDD_HHmmss')
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, `${t('REPORT.REPORT_NAME.PATIENT_TREATMENT')}_${timestamp}.xlsx`)
    } catch (error) {
        console.error('Error exporting to Excel:', error);
    }
}

export default ExportToExcel 